import React from "react";
import * as LucideIcons from "lucide-react";
import { Check, ArrowRight, Sparkles } from "lucide-react";

function Icon({ name, size = 22 }) {
    const Cmp = LucideIcons[name] || LucideIcons.Code2;
    return <Cmp size={size} />;
}

export default function Services({ items = [] }) {
    if (items.length === 0) return null;
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-24 md:py-32 border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div>
                        <div className="mono-label mb-4">03 · Services</div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tighter">
                            How we <span className="text-brand">collaborate</span>.
                        </h2>
                    </div>
                    <p className="text-ink-200 max-w-md">
                        Flexible software engineering engagements tailored to your technical scope — from modular system integrations to full-lifecycle product architecture.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4 lg:gap-5">
                    {items.map((s) => (
                        <div
                            key={s.id}
                            data-testid={`service-${s.name.toLowerCase().replace(/\s|&|\//g, "-")}`}
                            className={`group relative p-6 lg:p-8 rounded-2xl border bg-ink-800/40 hover:bg-ink-800 transition-all hover:-translate-y-1 ${
                                s.featured ? "border-brand/40" : "border-white/5 hover:border-brand/30"
                            }`}
                        >
                            {s.featured && (
                                <span className="absolute -top-2.5 left-6 font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-brand text-white">
                                    Most popular
                                </span>
                            )}
                            <div className="flex items-start justify-between gap-3 mb-5">
                                <div className="w-12 h-12 rounded-xl bg-brand/15 border border-brand/30 flex items-center justify-center text-brand">
                                    <Icon name={s.icon} size={20} />
                                </div>
                                {s.price_range && (
                                    <div className="text-right">
                                        <div className="mono-label">Starting at</div>
                                        <div className="text-white font-display text-lg">{s.price_range}</div>
                                    </div>
                                )}
                            </div>
                            <h3 className="font-display text-2xl text-white tracking-tight">{s.name}</h3>
                            {s.tagline && <p className="mt-1.5 text-sm text-ink-100">{s.tagline}</p>}
                            {s.description && <p className="mt-4 text-sm text-ink-200 leading-relaxed">{s.description}</p>}

                            {s.deliverables?.length > 0 && (
                                <ul className="mt-6 space-y-2.5 border-t border-white/5 pt-5">
                                    {s.deliverables.map((d, i) => (
                                        <li key={i} className="flex items-start gap-3 text-sm text-ink-100">
                                            <span className="mt-0.5 w-5 h-5 rounded-full bg-brand/15 border border-brand/30 flex items-center justify-center text-brand flex-shrink-0">
                                                <Check size={11} strokeWidth={3} />
                                            </span>
                                            {d}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <a
                                href="#contact"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="mt-6 inline-flex items-center gap-2 text-sm text-brand group-hover:gap-3 transition-all"
                            >
                                Discuss this <ArrowRight size={14} />
                            </a>
                        </div>
                    ))}
                </div>

                {/* Kodekami Studio Ecosystem Callout Banner */}
                <div className="mt-12 p-8 lg:p-10 rounded-2xl border border-brand/30 bg-gradient-to-br from-ink-800/80 via-ink-800/40 to-brand/10 backdrop-blur-md relative overflow-hidden">
                    <div className="absolute top-0 right-0 -mt-8 -mr-8 w-64 h-64 rounded-full bg-brand/10 blur-3xl pointer-events-none" />
                    <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                        <div className="space-y-2 max-w-2xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/15 border border-brand/30 text-brand text-xs font-mono tracking-wider uppercase">
                                <Sparkles size={12} />
                                <span>Dedicated Engineering Studio</span>
                            </div>
                            <h3 className="font-display text-2xl sm:text-3xl text-white tracking-tight">
                                Need a dedicated development team or full studio build?
                            </h3>
                            <p className="text-ink-200 text-sm sm:text-base leading-relaxed">
                                Beyond 1-on-1 architecture advisory, discover <span className="text-white font-medium">Kodekami</span> — my software studio delivering enterprise web systems, high-throughput APIs, turnkey SaaS, and continuous SLA support.
                            </p>
                        </div>
                        <a
                            href="https://kodekami.com"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="services-kodekami-cta"
                            className="group shrink-0 inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-brand text-white font-medium hover:bg-brand-hover transition-all shadow-[0_10px_30px_rgba(255,69,0,0.3)] hover:shadow-[0_12px_40px_rgba(255,69,0,0.4)]"
                        >
                            <span>Explore Kodekami Studio</span>
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
